// code-guardian-ignore-duplicates: shared presentational scaffolding (bulk-upload modal from the design system); sales-target twin of ProductMasterBulkUploadModal
import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import Modal from './Modal';
import { salesTargetsAPI, incentiveAPI } from '../services/api';
import FileDropZone from './FileDropZone';
import SalesSheetDownloadButton from './SalesSheetDownloadButton';

/**
 * Target / sales import for Master Data → Sales & Targets.
 *
 * ── WHAT MAKES THIS DIFFERENT FROM EVERY OTHER UPLOAD MODAL HERE ─────────────
 * The response is not just "N rows applied". Uploading store targets can create
 * a mistake that NO row-level check could have caught: a store target of 12 with
 * a multiplier of 0.6 gives each promoter 7, and a store holding two promoters
 * is then asked for 14 against a budget of 12. That depends on how many people
 * are deployed, which the sheet says nothing about — so the server applies the
 * upload, recalculates, and sends back what the calculation objected to. This
 * modal shows it immediately, and offers it as a file to work through.
 *
 * ── ONE FILE, EVERY PRODUCT ──────────────────────────────────────────────────
 * No upload here is per product. The monthly sheets have a column per product
 * and the per-sale sheet names a product on each row, so one file carries the
 * whole Product Master. The modal says so before the upload and, after it,
 * counts what was APPLIED product by product — a mistyped column header sets
 * nothing for that product while everything else goes in, and a total alone
 * would not show it.
 *
 * A monthly sheet never removes anything, so a product nothing was applied for
 * is as it was. The per-sale sheet is different: Replace deletes the named
 * promoters' month whatever the product, and Add replaces an earlier sale that
 * shares a Reference — so on that sheet the claim is made only for "replace
 * only the products in this sheet", which touches no other product.
 */

interface RowIssue {
  rowNumber: number;
  subjectCode: string;
  column?: string;
  message: string;
}

interface UploadResult {
  shape: 'wide' | 'long';
  applied: number;
  inserted: number;
  updated: number;
  issues: RowIssue[];
  /** How many row problems there were; `issues` lists only the first thousand. */
  issuesTotal?: number;
  unknownColumns: string[];
  /** Every product of the project, with how many figures (or sales) the upload applied for it. */
  products?: ProductTally[];
  /** Store / employee details the sheet carried for reference — read, never saved. */
  referenceColumns?: string[];
  /** Promoter rows on the per-sale sheet that carried no sale, so were ignored. */
  untouchedRows?: number;
  validation?: {
    warnings: { message: string; storeCode: string; productLabel: string }[];
    coverage: { linesExplicit: number; linesMissing: number; linesOnDefault: number; gapCount: number };
  };
  resolved?: { employeesWithSales: number; unattributed: number };
  /** The per-sale sheet's own account of what it did to the month. */
  detailed?: { mode: WriteMode; added: number; updated: number; removed: number; promoters: number };
}

interface ProductTally {
  label: string;
  figures: number;
}

type WriteMode = 'add' | 'replace' | 'replace_products';

/** Products named in full before the rest are counted — a version-level project has dozens. */
const NAMED_ABSENT_PRODUCTS = 12;

/**
 * How a per-sale sheet meets the sales already recorded for its month.
 *
 * Asked at upload, not buried in settings, because the same person sends both
 * kinds of file: the parts of a month as they arrive, and now and then a
 * corrected whole month. Add is the default — a month uploaded in three parts
 * used to keep only the third, because replacing was the only behaviour.
 */
const WRITE_MODES: { value: WriteMode; label: string; detail: string }[] = [
  {
    value: 'add',
    label: 'Add to what is already recorded',
    detail: 'For a month sent in parts — the 1st–10th, then the 11th–20th. Nothing already recorded is '
      + 'removed. A row whose Reference is already recorded, for the same promoter or at the same store, '
      + 'updates that sale instead of counting it twice, so every row needs a Reference. That holds whatever '
      + 'the product: when one Reference covers two products, keep both rows in the same file.',
  },
  {
    value: 'replace',
    label: 'Replace the month for the promoters in this sheet',
    detail: 'For one file holding the whole month of every promoter it names, for every product. Every sale '
      + 'already recorded for them this month is deleted first, whatever the product, including parts sent '
      + 'earlier. A promoter with no sale in the file is not changed. For a file that holds only some '
      + 'products, choose the option below.',
  },
  {
    value: 'replace_products',
    label: 'Replace only the products in this sheet',
    detail: 'For a file holding the whole month of one product, or a few. For every promoter who has a sale '
      + 'in the file, their sales of those products already recorded this month are deleted first, including '
      + 'parts sent earlier. Their sales of every other product are left as they are. A promoter with no sale '
      + 'in the file is not changed: sales recorded for them earlier stay.',
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
  month: string;
  scope: 'store' | 'employee';
  /** 'sales-detailed' is the per-SALE sheet, not the monthly one. */
  mode: 'target' | 'sales' | 'sales-detailed';
  /** The product chosen on the page — the download can be narrowed to it. */
  product?: { key: string; label: string } | null;
  /** How many products the project has. */
  productCount?: number;
}

export default function SalesTargetUploadModal({
  isOpen, onClose, onSuccess, projectId, month, scope, mode, product = null, productCount = 0,
}: Props) {
  const isDetailed = mode === 'sales-detailed';
  const isSales = mode === 'sales' || isDetailed;
  const noun = isDetailed ? 'per-sale rows' : isSales ? 'sales' : 'targets';

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The server's question when every Sale Date in the sheet is in another month.
  // Nothing has been recorded at that point; the answer is to change the month on
  // the page, or to say the month is right and send the sheet again.
  const [monthQuestion, setMonthQuestion] = useState<string | null>(null);
  const [writeMode, setWriteMode] = useState<WriteMode>('add');

  // The per-sale box is three options and a file tall, so on a laptop the result
  // lands below the fold of the box — brought into view rather than left to be found.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [result]);

  // Back to Add every time the modal closes: Replace deletes, so it should be
  // chosen for the file in hand, never inherited from the last upload.
  const reset = () => { setFile(null); setResult(null); setError(null); setMonthQuestion(null); setWriteMode('add'); };

  const handleFileSelected = (selected: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (!validTypes.includes(selected.type) && !selected.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please choose an Excel file (.xlsx, .xls) or a CSV.');
      setFile(null);
      return;
    }
    setFile(selected);
    setError(null);
    setMonthQuestion(null);
    setResult(null);
  };

  const handleUpload = async (confirmMonth = false) => {
    if (!file) { setError('Choose a file first.'); return; }
    setIsUploading(true);
    setError(null);
    setMonthQuestion(null);
    setResult(null);
    try {
      // The per-sale sheet goes up as the file itself; the monthly sheets still go as base64.
      const readBase64 = () => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const parts = text.split(',');
          if (parts.length < 2) reject(new Error('That file could not be read.'));
          else resolve(parts[1]);
        };
        reader.onerror = () => reject(new Error('That file could not be read.'));
        reader.readAsDataURL(file);
      });

      const res = isDetailed
        ? await (confirmMonth
          ? incentiveAPI.uploadDetailed(projectId, file, month, writeMode, true)
          : incentiveAPI.uploadDetailed(projectId, file, month, writeMode))
        : isSales
          ? await salesTargetsAPI.uploadSales(projectId, await readBase64(), month, scope)
          : await salesTargetsAPI.uploadTargets(projectId, await readBase64(), month, scope);
      // The detailed importer reports what the sheet did to the month — added,
      // updated, removed — rather than the monthly sheets' shape.
      const data = res.data.data ?? {};
      setResult(isDetailed
        ? {
          shape: 'long',
          applied: data.inserted ?? 0,
          inserted: data.inserted ?? 0,
          updated: data.updated ?? 0,
          issues: data.issues ?? [],
          issuesTotal: data.issuesTotal,
          unknownColumns: [],
          products: data.products,
          referenceColumns: data.referenceColumns ?? [],
          untouchedRows: data.untouchedRows ?? 0,
          detailed: {
            mode: data.mode === 'replace' || data.mode === 'replace_products' ? data.mode : 'add',
            added: data.added ?? 0,
            updated: data.updated ?? 0,
            removed: data.removed ?? 0,
            promoters: data.promoters ?? 0,
          },
        }
        : data);
      onSuccess();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'The upload failed.';
      if (err.response?.data?.code === 'SALE_MONTH_MISMATCH') setMonthQuestion(message);
      else setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  // Every row problem is counted; the server lists only the first thousand.
  const issueCount = result ? result.issuesTotal ?? result.issues.length : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title={`Upload ${noun} for ${month}`}
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          {/* Pre-filled with what is already set for the month: download, change a
              few cells, upload it back. A blank grid invites retyping every store,
              and retyping is where targets drift. */}
          <SalesSheetDownloadButton
            projectId={projectId}
            month={month}
            sheet={mode}
            scope={scope}
            product={product}
            productCount={productCount}
            label={isDetailed ? 'Download the per-sale sheet' : "Download this month's sheet"}
            placement="above"
            onError={setError}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { reset(); onClose(); }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {result ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => handleUpload()}
              disabled={!file || isUploading}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
              Upload
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {isDetailed ? (
          <p className="text-sm text-gray-600">
            One row per sale, each with its own Reference. One file takes every product — the
            Product column on each row says which. {month} can be sent in parts — choose how this
            sheet meets the sales already recorded.
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            The sheet comes pre-filled with whatever is already set for {month} — change the cells you
            need and upload it back. A blank cell leaves the existing figure alone; a zero is a real
            figure of zero. Re-uploading is safe and never creates duplicates.
            {isSales && ' Recording last month’s sales now is the normal way to use this.'}
            {productCount > 1 && (
              <>
                {' '}
                <strong className="font-medium text-gray-800">
                  One file can carry every product, or only some
                </strong>
                : each product column in it is applied, and a product with no column is left as it
                is.
              </>
            )}
          </p>
        )}

        {isDetailed && (
          <fieldset className="space-y-2">
            <legend className="sr-only">How this sheet meets the sales already recorded</legend>
            {WRITE_MODES.map((o) => (
              <label
                key={o.value}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                  writeMode === o.value ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="detailed-write-mode"
                  className="mt-1"
                  checked={writeMode === o.value}
                  onChange={() => { setWriteMode(o.value); setResult(null); setMonthQuestion(null); }}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{o.label}</span>
                  <span className="block text-xs text-gray-600">{o.detail}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <FileDropZone
          inputId="sales-target-upload"
          file={file}
          onFile={handleFileSelected}
          onClear={() => { setFile(null); setMonthQuestion(null); }}
        />

        {monthQuestion && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p>{monthQuestion}</p>
              <button
                type="button"
                onClick={() => handleUpload(true)}
                disabled={!file || isUploading}
                className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Record them for {month} anyway
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div ref={resultRef} className="space-y-3">
            <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {result.detailed?.mode === 'add' ? (
                <span>
                  <strong>{result.detailed.added.toLocaleString()}</strong> new sale
                  {result.detailed.added === 1 ? '' : 's'} added and{' '}
                  <strong>{result.detailed.updated.toLocaleString()}</strong> already recorded updated
                  {result.detailed.removed > 0 && (
                    <>
                      ; <strong>{result.detailed.removed.toLocaleString()}</strong> earlier row
                      {result.detailed.removed === 1 ? '' : 's'} under the same References removed
                    </>
                  )}
                  .
                </span>
              ) : result.detailed ? (
                <span>
                  <strong>{result.inserted.toLocaleString()}</strong> sale{result.inserted === 1 ? '' : 's'}{' '}
                  recorded for <strong>{result.detailed.promoters.toLocaleString()}</strong> promoter
                  {result.detailed.promoters === 1 ? '' : 's'}, replacing the{' '}
                  <strong>{result.detailed.removed.toLocaleString()}</strong> recorded for them before
                  {result.detailed.mode === 'replace_products' && ' for the products in this sheet'}.
                </span>
              ) : (
                <span>
                  <strong>{result.inserted}</strong> added and <strong>{result.updated}</strong> updated
                  {result.shape === 'long' && ' (read as a long-format sheet)'}.
                </span>
              )}
            </div>

            {result.unknownColumns.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-medium">
                  {result.unknownColumns.length} column
                  {result.unknownColumns.length === 1 ? '' : 's'} did not match a product and
                  {result.unknownColumns.length === 1 ? ' was' : ' were'} ignored
                </p>
                <p className="mt-1 text-amber-800">{result.unknownColumns.join(', ')}</p>
                <p className="mt-1 text-amber-800">
                  Download the sheet above to get the exact column names for this project.
                </p>
              </div>
            )}

            {/* Counted by what was APPLIED, not by what the file held: a refused
                cell or a mistyped header also leaves a product at nothing. Skipped
                for a one-product project, where the total above is that product. */}
            {result.products && result.products.length > 1 && (() => {
              const applied = result.products.filter((p) => p.figures > 0);
              const none = result.products.filter((p) => p.figures === 0).map((p) => p.label);
              const unit = isDetailed ? 'sale' : 'figure';
              return (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  <p className="font-medium text-gray-900">
                    {applied.length === 0
                      ? (isDetailed ? 'No sale from this file was recorded' : 'No figure from this file was applied')
                      : `${isDetailed ? 'Sales were recorded' : 'Figures were applied'} for ${applied.length} of ${result.products.length} products`}
                  </p>
                  {applied.length > 0 && (
                    <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      {applied.map((p) => (
                        <li key={p.label}>
                          {p.label}{' '}
                          <strong>{p.figures.toLocaleString()}</strong> {unit}{p.figures === 1 ? '' : 's'}
                        </li>
                      ))}
                    </ul>
                  )}
                  {applied.length > 0 && none.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      {isDetailed ? 'No sale was recorded for ' : 'No figure was applied for '}
                      {none.slice(0, NAMED_ABSENT_PRODUCTS).join(', ')}
                      {none.length > NAMED_ABSENT_PRODUCTS && ` and ${none.length - NAMED_ABSENT_PRODUCTS} more`}
                      {!isDetailed || result.detailed?.mode === 'replace_products'
                        ? ` — left as ${none.length === 1 ? 'it was' : 'they were'}.`
                        : result.detailed?.mode === 'replace'
                          ? `. Replace removed any sales of ${none.length === 1 ? 'it' : 'them'} already `
                            + 'recorded this month for the promoters in this file.'
                          : '.'}
                    </p>
                  )}
                </div>
              );
            })()}

            {result.untouchedRows !== undefined && result.untouchedRows > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <strong>{result.untouchedRows.toLocaleString()}</strong> promoter row
                {result.untouchedRows === 1 ? '' : 's'} had no sale filled in, so{' '}
                {result.untouchedRows === 1 ? 'it was' : 'they were'}{' '}
                {/* A blank row is not "this promoter sold nothing": either Replace
                    deletes only for promoters who have a sale in the file. */}
                {result.detailed && result.detailed.mode !== 'add'
                  ? `skipped. Nothing was deleted for ${result.untouchedRows === 1 ? 'that promoter' : 'those promoters'}: `
                    + 'any sales recorded for them earlier are still there.'
                  : 'ignored. That is expected — the sheet lists every promoter who worked the month.'}
              </div>
            )}

            {result.referenceColumns && result.referenceColumns.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <p>
                  <span className="font-medium">Read for reference only, not saved:</span>{' '}
                  {result.referenceColumns.join(', ')}.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  These are store and employee details. To change one, edit it in Master Data.
                </p>
              </div>
            )}

            {/* Row problems, listed with their spreadsheet row number so the admin
                can open the file and go straight to the cell. Scrolls rather
                than truncating: a 40-row problem is still 40 things to fix. */}
            {result.issues.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-amber-900">
                      {issueCount.toLocaleString()} row{issueCount === 1 ? '' : 's'} could not be
                      read and {issueCount === 1 ? 'was' : 'were'} skipped
                    </p>
                    <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-amber-200 bg-white">
                      <table className="min-w-full text-xs">
                        <thead className="bg-amber-50 text-left text-amber-900">
                          <tr>
                            <th className="px-3 py-2">Row</th>
                            <th className="px-3 py-2">{scope === 'store' ? 'Store' : 'Employee'}</th>
                            <th className="px-3 py-2">Column</th>
                            <th className="px-3 py-2">Problem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {result.issues.slice(0, 100).map((i, idx) => (
                            <tr key={`${i.rowNumber}-${i.column ?? ''}-${idx}`}>
                              <td className="px-3 py-1.5 font-medium">{i.rowNumber}</td>
                              <td className="px-3 py-1.5">{i.subjectCode || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-600">{i.column || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{i.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {issueCount > 100 && (
                      <p className="mt-1 text-xs text-amber-800">
                        Showing the first 100 of {issueCount.toLocaleString()}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {result.validation && result.validation.coverage.linesMissing > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <strong>{result.validation.coverage.linesMissing.toLocaleString()}</strong>{' '}
                {scope === 'store' ? 'store' : 'promoter'}/product combinations still have no target
                for {month}
                {result.validation.coverage.linesOnDefault > 0
                  && `, and ${result.validation.coverage.linesOnDefault.toLocaleString()} are running on a default`}
                . The Coverage tab lists them.
              </div>
            )}

            {result.resolved && result.resolved.unattributed > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {result.resolved.unattributed} store sales figure
                {result.resolved.unattributed === 1 ? '' : 's'} reached no promoter — the shop stood
                empty on those days. The figures are recorded against the store either way.
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
